import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore }    from '../store/authStore.js';
import { useSessionStore } from '../store/sessionStore.js';
import { usePrinterStore } from '../store/printerStore.js';
import { getTipsByWaiter, getBusinessInfo } from '../api/pos.api.js';
import { thermalPrinter }  from '../native/thermalPrinter.js';
import { IcoCheck, IcoArrowUp, IcoArrowDown, IcoPrint } from '../components/common/Icons.jsx';
import styles from './CorteScreen.module.css';

const fmt     = (n) => `$${Number(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '—';

export default function CorteScreen() {
  const navigate   = useNavigate();
  const { state }  = useLocation();
  const user       = useAuthStore((s) => s.user);
  const closeSess  = useSessionStore((s) => s.closeSession);

  const session      = state?.session;
  const terminal     = state?.terminal;
  const printerDevId = usePrinterStore((s) => s.deviceId);
  const printerTrans = usePrinterStore((s) => s.transport);
  const [business,   setBusiness]   = useState(null);
  const [btPrinting, setBtPrinting] = useState(false);
  const [btError,    setBtError]    = useState(null);
  const [propinas,   setPropinas]   = useState(null);

  useEffect(() => {
    // Sin `state` no hay corte que mostrar: se llegó por URL o por un
    // recargado. Antes se cerraba la sesión local de todos modos y la caja
    // se quedaba sin turno sin que nadie hubiera cerrado nada.
    if (!session) { navigate('/open-session', { replace: true }); return; }
    closeSess();
    getBusinessInfo().then(setBusiness).catch(() => {});
    // Solo gerencia puede consultarlo; si el corte lo cierra un cajero, la
    // sección simplemente no aparece en vez de mostrar un error.
    if (session?._id) {
      getTipsByWaiter({ session_id: session._id }).then(setPropinas).catch(() => setPropinas(null));
    }
  }, []);

  if (!session) return null;

  const diff = session.closing_cash != null
    ? session.closing_cash - (session.expected_cash ?? 0)
    : null;


  const handleBtPrint = async () => {
    setBtError(null);
    if (printerDevId && !thermalPrinter.isConnected()) {
      await thermalPrinter.autoReconnect(printerDevId, printerTrans).catch(() => {});
    }
    setBtPrinting(true);
    try {
      await thermalPrinter.printCorte({
        business,
        cashier:      user?.name ?? user?.email ?? null,
        terminal:     terminal?.name ?? null,
        openedAt:     session.opened_at ? new Date(session.opened_at).toLocaleString('es-MX') : null,
        closedAt:     session.closed_at ? new Date(session.closed_at).toLocaleString('es-MX') : null,
        salesCount:   session.sales_count,
        salesTotal:   session.sales_total,
        openingCash:  session.opening_cash,
        closingCash:  session.closing_cash,
        expectedCash: session.expected_cash,
        breakdown:    session.payment_breakdown,
        diff,
        notes:        session.notes,
      });
    } catch (err) {
      setBtError('Error al imprimir: ' + (err.message ?? 'Intenta de nuevo'));
    } finally {
      setBtPrinting(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.inner}>

        {/* Header del negocio — visible solo al imprimir */}
        <div className={styles.printHeader}>
          <p className={styles.printBusiness}>{business?.name ?? ''}</p>
          {business?.rfc && <p className={styles.printMeta}>RFC: {business.rfc}</p>}
          {business?.address && <p className={styles.printMeta}>{business.address}</p>}
          <p className={styles.printMeta}>Corte de caja · {terminal?.name ?? ''}</p>
        </div>

        <div className={styles.header}>
          <div className={styles.checkIcon}><IcoCheck size={22} /></div>
          <div>
            <h1 className={styles.title}>Caja cerrada</h1>
            <p className={styles.subtitle}>Corte de caja · {terminal?.name ?? 'Terminal'}</p>
          </div>
        </div>

        {/* Resumen */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Resumen del turno</h2>
          <div className={styles.grid}>
            <Row label="Cajero"           value={user?.name ?? user?.email ?? '—'} />
            <Row label="Apertura"         value={fmtDate(session.opened_at)} />
            <Row label="Cierre"           value={fmtDate(session.closed_at)} />
            <Row label="Efectivo inicial" value={fmt(session.opening_cash)} />
          </div>
        </section>

        {/* Ventas */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Ventas</h2>
          <div className={styles.grid}>
            <Row label="Transacciones" value={session.sales_count ?? 0} />
            <Row label="Total vendido"  value={fmt(session.sales_total)} bold />
          </div>
        </section>

        {/* Desglose por forma de pago */}
        {session.payment_breakdown && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Desglose por forma de pago</h2>
            <div className={styles.grid}>
              {session.payment_breakdown.cash > 0 && (
                <Row label="Efectivo"       value={fmt(session.payment_breakdown.cash)} />
              )}
              {session.payment_breakdown.card > 0 && (
                <Row label="Tarjeta"        value={fmt(session.payment_breakdown.card)} />
              )}
              {session.payment_breakdown.transfer > 0 && (
                <Row label="Transferencia"  value={fmt(session.payment_breakdown.transfer)} />
              )}
              {session.payment_breakdown.tips > 0 && (
                <Row
                  label="Propinas (incluidas en el efectivo esperado)"
                  value={fmt(session.payment_breakdown.tips)}
                />
              )}
              {session.payment_breakdown.cash === 0 &&
               session.payment_breakdown.card === 0 &&
               session.payment_breakdown.transfer === 0 && (
                <Row label="Sin ventas registradas" value="—" />
              )}
            </div>
          </section>
        )}

        {/* Reparto de propinas: la propina es de quien atendió la mesa, no de
            quien cobró, así que este desglose no coincide con quién registró
            cada pago — es justo el punto. */}
        {propinas && propinas.waiters.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Propinas por mesero</h2>
            <div className={styles.rows}>
              {propinas.waiters.map((w) => (
                <Row
                  key={w.waiter_id ?? 'sin-asignar'}
                  label={`${w.waiter_name} · ${w.count} cobro${w.count !== 1 ? 's' : ''}`}
                  value={`${fmt(w.tips)}  (${w.share_pct}%)`}
                />
              ))}
              <Row label="Total de propinas" value={fmt(propinas.total)} bold />
            </div>
          </section>
        )}

        {/* Efectivo */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Efectivo</h2>
          <div className={styles.grid}>
            <Row label="Esperado en caja" value={fmt(session.expected_cash)} />
            <Row label="Contado al cerrar" value={session.closing_cash != null ? fmt(session.closing_cash) : 'No registrado'} />
            {diff !== null && (
              <div className={`${styles.diff} ${diff < 0 ? styles.diffNeg : diff > 0 ? styles.diffPos : styles.diffZero}`}>
                {diff === 0
                  ? <><IcoCheck size={13} /> Sin diferencia</>

                  : diff > 0
                  ? `+${fmt(diff)} sobrante`
                  : `${fmt(diff)} faltante`}
              </div>
            )}
            {/* La caja central cerró sin poder consultar la nube. Las
                devoluciones que pasaron por ella ya están descontadas, pero una
                hecha desde otro lado —el panel web, otra tablet— podría no
                estarlo. Se dice aquí porque es justo donde aparecería como un
                faltante que el cajero no tiene. `=== false` y no `!` a
                propósito: sin caja central el dato no viene y no hay nada que
                advertir. */}
            {session.devoluciones_al_dia === false && (
              <p className={styles.notes}>
                Se cerró sin conexión con la nube: si durante el turno se hizo una devolución desde otro
                lugar, podría no estar descontada del esperado.
              </p>
            )}
          </div>
        </section>

        {/* Movimientos de caja */}
        {session.movements?.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Movimientos de caja</h2>
            {session.movements.map((m, i) => (
              <div key={i} className={`${styles.row} ${m.type === 'out' ? styles.rowOut : styles.rowIn}`}>
                <span className={styles.rowLabel}>
                  {m.type === 'in' ? <IcoArrowUp size={12} /> : <IcoArrowDown size={12} />} {m.concept || (m.type === 'in' ? 'Entrada' : 'Salida')}
                  <span className={styles.movTime}> · {fmtTime(m.createdAt)}</span>
                </span>
                <span className={styles.rowValue}>{m.type === 'out' ? '−' : '+'}{fmt(m.amount)}</span>
              </div>
            ))}
            <div className={styles.row} style={{ borderTop: '1.5px solid var(--border)', marginTop: 4, paddingTop: 8 }}>
              <span className={styles.rowLabel}>Neto movimientos</span>
              <span className={`${styles.rowValue} ${styles.rowBold}`}>
                {fmt(session.movements.reduce((s, m) => s + (m.type === 'in' ? m.amount : -m.amount), 0))}
              </span>
            </div>
          </section>
        )}

        {session.notes && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Notas</h2>
            <p className={styles.notes}>{session.notes}</p>
          </section>
        )}

        {btError && <p className={styles.btError}>{btError}</p>}

        <div className={styles.actions}>
          <button
            className="btn btn--secondary btn--full"
            onClick={(printerDevId || thermalPrinter.isConnected()) ? handleBtPrint : () => window.print()}
            disabled={btPrinting}
          >
            {btPrinting ? <span className="spinner" /> : <><IcoPrint size={14} /> Imprimir corte</>}
          </button>
          <button className="btn btn--primary btn--full" onClick={() => navigate('/open-session', { replace: true })}>
            Nueva sesión
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={`${styles.rowValue} ${bold ? styles.rowBold : ''}`}>{value}</span>
    </div>
  );
}
